import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Users, CalendarClock, CheckCircle2, Check, X, CalendarPlus } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, EmptyState, PageHeader, PageLoader, Panel, SearchInput, StatCard, StatusBadge } from "@/components/common";
import { TutorSessionTable } from "./TutorSessions";

export function TutorDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/tutor/dashboard").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <PageLoader />;
  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ").slice(-1)[0]}`} subtitle="Your teaching overview."
        actions={<Link to="/tutor/schedule" className="inline-flex items-center gap-2 rounded-full bg-[#1F6FFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1450C9]" data-testid="dashboard-schedule-button"><CalendarPlus className="h-4 w-4" />Schedule session</Link>} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        <StatCard label="Pending Requests" value={d.pending_requests} icon={Send} tone="amber" testid="tutor-stat-pending" />
        <StatCard label="Approved Students" value={d.approved_students} icon={Users} testid="tutor-stat-approved" />
        <StatCard label="Upcoming Sessions" value={d.upcoming_count} icon={CalendarClock} tone="cyan" testid="tutor-stat-upcoming" />
        <StatCard label="Completed Sessions" value={d.completed_count} icon={CheckCircle2} tone="green" testid="tutor-stat-completed" />
      </div>
      <Panel className="mt-8">
        <div className="flex items-center justify-between p-6 pb-2"><h3 className="text-lg font-bold text-[#0B1A33]">Upcoming sessions</h3>
          <Link to="/tutor/upcoming" className="text-sm font-semibold text-[#1F6FFF]">View all</Link></div>
        {d.upcoming.length ? <TutorSessionTable items={d.upcoming} refresh={load} /> : <div className="p-6"><EmptyState title="No upcoming sessions" text="Approve students and schedule your first session." /></div>}
      </Panel>
    </div>
  );
}

export function TutorRequests() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState([]);
  const load = useCallback(() => api.get("/tutor/requests", { params: { status: status === "all" ? "" : status } }).then((r) => { setItems(r.data); setSel([]); }), [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { const r = await fn(); toast.success(r.data.message); load(); } catch (e) { toast.error(errMsg(e)); } };
  const pending = (items || []).filter((r) => r.status === "pending");
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div>
      <PageHeader title="Student Requests" subtitle="Approve one or many students at once."
        actions={sel.length > 0 && (<>
          <Button onClick={() => act(() => api.post("/tutor/requests/bulk", { ids: sel, action: "approve" }))} className="rounded-full bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" data-testid="bulk-approve-button">Approve {sel.length} selected</Button>
          <Button variant="outline" onClick={() => act(() => api.post("/tutor/requests/bulk", { ids: sel, action: "reject" }))} className="rounded-full text-red-600" data-testid="bulk-reject-button">Reject selected</Button>
        </>)} />
      <Tabs value={status} onValueChange={setStatus} className="mb-4"><TabsList>
        {["pending", "approved", "rejected", "all"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`tutor-requests-tab-${s}`}>{s}</TabsTrigger>)}
      </TabsList></Tabs>
      <Panel>
        {!items ? <PageLoader /> : items.length === 0 ? <div className="p-6"><EmptyState title="No requests" /></div> : (
          <div className="overflow-x-auto"><Table data-testid="tutor-requests-table">
            <TableHeader><TableRow>
              <TableHead className="w-10">{pending.length > 0 && <Checkbox checked={sel.length === pending.length} onCheckedChange={(c) => setSel(c ? pending.map((r) => r.id) : [])} data-testid="select-all-requests" />}</TableHead>
              <TableHead>Student</TableHead><TableHead>Education</TableHead><TableHead>Message</TableHead><TableHead>Received</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{items.map((r) => (
              <TableRow key={r.id} data-testid={`tutor-request-row-${r.id}`}>
                <TableCell>{r.status === "pending" && <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`select-request-${r.id}`} />}</TableCell>
                <TableCell><div className="flex items-center gap-3"><Avatar src={r.student_picture} name={r.student_name} size="h-9 w-9" /><span className="font-semibold">{r.student_name}</span></div></TableCell>
                <TableCell>{r.student_education || "—"}</TableCell>
                <TableCell className="max-w-xs truncate text-sm text-slate-500">{r.message || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(r.created_at)}</TableCell>
                <TableCell><StatusBadge status={r.status} testid={`tutor-request-status-${r.id}`} /></TableCell>
                <TableCell><div className="flex justify-end gap-2">{r.status === "pending" && (<>
                  <Button size="sm" className="rounded-full bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" onClick={() => act(() => api.post(`/tutor/requests/${r.id}/approve`))} data-testid={`tutor-approve-request-button-${r.id}`}><Check className="h-4 w-4" />Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => act(() => api.post(`/tutor/requests/${r.id}/reject`))} data-testid={`tutor-reject-request-button-${r.id}`}><X className="h-4 w-4" /></Button>
                </>)}</div></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Panel>
    </div>
  );
}

export function ApprovedStudents() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  useEffect(() => { api.get("/tutor/students").then((r) => setItems(r.data)); }, []);
  const shown = (items || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div>
      <PageHeader title="Approved Students" subtitle="Select students to create a group session."
        actions={<Button disabled={!sel.length} onClick={() => nav("/tutor/schedule", { state: { studentIds: sel } })} className="rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="schedule-selected-button"><CalendarPlus className="h-4 w-4" />Schedule with {sel.length || ""} selected</Button>} />
      <Panel>
        <div className="border-b border-slate-100 p-4"><SearchInput value={q} onChange={setQ} placeholder="Search students" testid="approved-students-search" /></div>
        {!items ? <PageLoader /> : shown.length === 0 ? <div className="p-6"><EmptyState title="No approved students yet" text="Approve incoming requests to see students here." /></div> : (
          <div className="overflow-x-auto"><Table data-testid="approved-students-table">
            <TableHeader><TableRow><TableHead className="w-10" /><TableHead>Student</TableHead><TableHead>Education</TableHead><TableHead>Credit eligibility</TableHead><TableHead>Approved</TableHead></TableRow></TableHeader>
            <TableBody>{shown.map((s) => (
              <TableRow key={s.id} data-testid={`approved-student-row-${s.id}`}>
                <TableCell><Checkbox checked={sel.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`select-student-${s.id}`} /></TableCell>
                <TableCell><div className="flex items-center gap-3"><Avatar src={s.profile_picture} name={s.name} size="h-9 w-9" /><div><p className="font-semibold">{s.name}</p><p className="text-xs text-slate-500">{s.email}</p></div></div></TableCell>
                <TableCell>{s.education_level || "—"}</TableCell>
                <TableCell>{s.eligible ? <StatusBadge status="approved" /> : <span className="text-xs font-semibold text-red-600">Insufficient credits</span>}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(s.approved_at)}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Panel>
    </div>
  );
}
